import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { classSet } from '@umu/umu-dom';
import { Iconfont, Button, DatePicker, COMPOENT_THEME } from '@umu/umu-design';
import { lang } from '@umu/ukit-lang';
import { datePickerConfig } from '@umu/umu-util';
import { IMobileBasicInfo, PickerPanelType, DateMode } from '../../interface';
import { defaultDateFormat, defaultPlaceholder } from '../../dataFlow';
import { RenderDay } from './renderDay';

interface IProps extends IMobileBasicInfo {
    onClose?: () => void;
}

const { defaultPickerConfig } = datePickerConfig;

const PickerPanel = (props: IProps) => {
    const {
        preCls,
        defaultValue,
        mode = DateMode.multiple,
        clearBtn,
        placeholder = defaultPlaceholder,
        dateFormat = defaultDateFormat,
        theme = COMPOENT_THEME.GOLDEN,
        disabledDate,
        onClose,
        onChange,
    } = props;
    const isBlueTheme = theme === COMPOENT_THEME.BLUE;
    const isSignal = mode === DateMode.signal;
    const [value, setValue] = useState(defaultValue);
    const [st = null, et = null] = value || [];
    const [selectIndex, setSelectIndex] = useState<PickerPanelType>(PickerPanelType.start);
    const onDatePickerChange = (date: Dayjs) => {
        if (isSignal) {
            setValue([date, date]);
            return;
        } else {
            const isBeforeStartDate = selectIndex === PickerPanelType.end && st && date.isBefore(st, 'day');
            const isAfterEndDate = selectIndex === PickerPanelType.start && et && date.isAfter(et, 'day');
            if (isBeforeStartDate || isAfterEndDate) {
                return;
            }

            if (selectIndex === PickerPanelType.start) {
                setValue([date, et]);
            } else {
                setValue([st, date]);
            }
            const newPickerPanelType =
                selectIndex === PickerPanelType.start ? PickerPanelType.end : PickerPanelType.start;
            setSelectIndex(newPickerPanelType);
        }
    };
    const onChangeIndex = (index: PickerPanelType) => {
        setSelectIndex(index);
    };
    const expandConfig = {
        dateRender: (current: Dayjs) => {
            return (
                <RenderDay
                    info={current}
                    mode={mode}
                    disabledDate={disabledDate}
                    dateFormat={dateFormat}
                    value={value}
                    selectIndex={selectIndex}
                />
            );
        },
        disabledDate,
        onSelect: onDatePickerChange,
    };
    const renderInputContent = () => {
        return (
            <div className={`${preCls}-picker-input`}>
                <div
                    className={classSet({
                        [`${preCls}-input-normal`]: true,
                        [`${preCls}-input-active`]: selectIndex === PickerPanelType.start,
                    })}
                    onClick={() => onChangeIndex(PickerPanelType.start)}
                >
                    <div
                        className={classSet({
                            [`${preCls}-input-info`]: true,
                            'has-value': !!value?.[0],
                        })}
                    >
                        {value?.[0] ? dayjs(value?.[0]).format(dateFormat) : placeholder[0]}
                    </div>
                </div>
                <span className="input-split">-</span>
                <div
                    className={classSet({
                        [`${preCls}-input-normal`]: true,
                        [`${preCls}-input-active`]: selectIndex === PickerPanelType.end,
                    })}
                    onClick={() => onChangeIndex(PickerPanelType.end)}
                >
                    <div
                        className={classSet({
                            [`${preCls}-input-info`]: true,
                            'has-value': !!value?.[1],
                        })}
                    >
                        {value?.[1] ? dayjs(value?.[1]).format(dateFormat) : placeholder[1]}
                    </div>
                </div>
            </div>
        );
    };
    const onCLearClick = () => {
        setValue(undefined);
        if (clearBtn?.onClick) {
            clearBtn?.onClick({
                onClose,
                onChange,
            });
        } else {
            onClose?.();
            onChange?.(undefined);
        }
    };
    return (
        <div className={`${preCls} ${mode}`} onClick={(e) => e?.stopPropagation?.()}>
            <div className={`${preCls}-info`}>
                <span className="picker-title">{isSignal ? lang('选择日期') : lang('选择时间范围')}</span>
                <Iconfont type="Close" className="panel-icon-close" onClick={onClose} />
            </div>
            <div className={`${preCls}-picker ${mode}-picker`}>
                {!isSignal && renderInputContent()}
                <DatePicker
                    theme={theme}
                    prefixCls={`${preCls}-info`}
                    {...defaultPickerConfig}
                    width="100%"
                    isOnlyShowPickPanel
                    hideFooter
                    className={classSet({
                        [`${preCls}-content`]: true,
                    })}
                    expandConfig={expandConfig}
                />
            </div>
            <div className={`${preCls}-footer-btn`}>
                {clearBtn?.show && (
                    <Button
                        type={clearBtn?.type || 'ghost-grey'}
                        className="btn-split flex-full"
                        onClick={onCLearClick}
                    >
                        {clearBtn?.text || lang('不限')}
                    </Button>
                )}
                <Button
                    type={isBlueTheme ? 'primary-blue' : 'primary'}
                    className="flex-full"
                    onClick={() => onChange?.(value)}
                >
                    {lang('选择')}
                </Button>
            </div>
        </div>
    );
};

export { PickerPanel };
